import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authService, publicService } from '../api/services';
import toast from 'react-hot-toast';

const VIDEO_URL = 'https://firebasestorage.googleapis.com/v0/b/images-1aaab.appspot.com/o/20546634-uhd_3840_2160_30fps.mp4?alt=media&token=43e2bb92-2523-416f-a0b3-a1c42a351537';

const LoginPage = () => {
  const [show,    setShow]    = useState(false);
  const [form,    setForm]    = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [stats,   setStats]   = useState({ experts: null, sessions: null, clients: null });
  const { login } = useAuth();
  const navigate  = useNavigate();

  useEffect(() => {
    publicService.getStats()
      .then(res => {
        const d = res.data?.data ?? res.data;
        setStats({ experts: d.experts, sessions: d.sessions, clients: d.clients });
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authService.login(form);
      const { accessToken, refreshToken, user } = res.data.data;
      login(user, { accessToken, refreshToken });
      toast.success(`Welcome back, ${user.firstName}!`);

      if (user.role === 'SUPER_ADMIN') {
        navigate('/admin');
      } else if (user.role === 'EXPERT') {
        navigate('/expert/dashboard');
      } else if (user.role === 'CLIENT') {
        if (!user.assessmentCompleted) {
          navigate('/assessment');
        } else {
          navigate('/dashboard');
        }
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const statItems = [
    { value: stats.experts  !== null ? `${stats.experts}+`  : '—', sub: 'Verified Experts'  },
    { value: stats.sessions !== null ? `${stats.sessions}+` : '—', sub: 'Sessions Done'      },
    { value: stats.clients  !== null ? `${stats.clients}+`  : '—', sub: 'Entrepreneurs'      },
  ];

  return (
    <div className="min-h-screen flex">

      {/* ── Left — Video panel ── */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col justify-between p-12">

        <video autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover" style={{ zIndex: 0 }}>
          <source src={VIDEO_URL} type="video/mp4" />
        </video>

        <div className="absolute inset-0"
          style={{ zIndex: 1, background: 'linear-gradient(160deg, rgba(4,16,8,0.55) 0%, rgba(6,22,12,0.50) 50%, rgba(3,12,6,0.60) 100%)' }} />

        <div className="absolute top-0 left-0 right-0 h-0.5"
          style={{ zIndex: 2, background: 'linear-gradient(90deg, #dc2626, #d97706, #16a34a, #d97706, #dc2626)' }} />

        <div className="relative flex flex-col justify-between h-full" style={{ zIndex: 2 }}>

          <Link to="/" className="flex items-center gap-3 group">
            <div className="flex items-baseline gap-0.5">
              <span className="font-serif font-semibold text-forest-900 text-xl">TourEnt</span>
              <span className="font-serif font-semibold text-forest-500 text-xl"> Hub</span>
            </div>
            <div className="hidden sm:block text-[9px] tracking-[0.18em] uppercase text-white font-sans border-l border-forest-300 pl-3 ml-1">
              Tourism · Expertise · Growth
            </div>
          </Link>

          <div className="space-y-4">
            <h2 className="font-serif text-4xl text-white leading-snug" style={{ fontFamily: 'Georgia, serif' }}>
              Welcome back to Tourent Hub <br />
              <span className="italic" style={{ color: '#86efac' }}>The Entrepreneurship Capacity Building Platform</span>
            </h2>
            <p className="text-sm leading-relaxed max-w-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Connect with verified specialists and grow your business with guided expertise. Book live sessions, get personalized support and access exclusive content to level up your entrepreneurial journey.
            </p>
          </div>

          <div className="flex gap-3">
            {statItems.map((s) => (
              <div key={s.sub} className="flex-1 text-center rounded-2xl p-4"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="font-serif text-xl font-bold" style={{ color: '#86efac', fontFamily: 'Georgia, serif' }}>{s.value}</div>
                <div className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right — Form panel ── */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-cream-50">
        <div className="w-full max-w-md space-y-8">

          <Link to="/" className="flex items-center gap-3 group">
            <div className="flex items-baseline gap-0.5">
              <span className="font-serif font-semibold text-xl transition-colors duration-300 text-black">TourEnt</span>
              <span className="font-serif font-semibold text-xl transition-colors duration-300 text-green-400"> Hub</span>
            </div>
            <div className="hidden sm:block text-[9px] tracking-[0.18em] uppercase font-sans border-l pl-3 ml-1 transition-colors duration-300 text-forest-400 border-forest-200">
              Tourism · Expertise · Growth
            </div>
          </Link>

          <div>
            <h1 className="font-serif text-3xl text-forest-900">Sign in</h1>
            <p className="text-forest-500 text-sm mt-1">Enter your credentials to access your account.</p>
          </div>

          {error && (
            <div className="flex items-start gap-3 bg-rust-50 border border-rust-200 text-rust-600 rounded-xl px-4 py-3">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-forest-700 mb-1.5">Email address</label>
              <input type="email" required value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
            </div>

            <div>
              <label className="block text-sm font-medium text-forest-700 mb-1.5">Password</label>
              <div className="relative">
                <input type={show ? 'text' : 'password'} required value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 pr-11 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
                <button type="button" onClick={() => setShow(!show)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-forest-400 hover:text-forest-700">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="text-right mt-1.5">
                <Link to="/forgot-password" className="text-xs text-forest-500 hover:text-forest-800">Forgot password?</Link>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="btn-primary w-full justify-center mt-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none">
              {loading
                ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Signing in...</>
                : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-sm text-forest-500">
            Don't have an account?{' '}
            <Link to="/register" className="text-forest-800 font-medium hover:underline">Create one →</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;